// POSH: robust collection card video injector for USF + theme grids
(function () {
  const VIDEO_EXT_REGEX = /\.(mp4|webm)(\?|$)/i;
  if (typeof window.POSH_DISABLE_VIEW_FETCH === 'undefined') {
    window.POSH_DISABLE_VIEW_FETCH = false; // default: allow per-tile fetch fallback
  }

  function normalizeUrl(u) {
    if (!u) return u;
    return u.indexOf('//') === 0 ? (location.protocol + u) : u;
  }

  const DBG = !!window.POSH_DEBUG;
  function dbg(){ if (DBG) try { console.log('[POSH]', ...arguments); } catch(e){} }

  const _poshAttemptedFetch = new Set();

  function getPid(card) {
    return (
      card.getAttribute('data-usf-pid') ||
      card.getAttribute('data-product-id') ||
      // sometimes USF nests a product-block with the attrs inside the card
      (card.querySelector('[data-usf-pid]')?.getAttribute('data-usf-pid')) ||
      (card.querySelector('[data-product-id]')?.getAttribute('data-product-id')) ||
      null
    );
  }

  function getMappedVideo(card) {
    const pid = getPid(card);
    if (!pid) return { mp4: null, iframe: null };
    const mp4 = window.POSH_PRODUCT_VIDEO_SRCS && window.POSH_PRODUCT_VIDEO_SRCS[pid];
    const iframe = window.POSH_PRODUCT_IFRAMES && window.POSH_PRODUCT_IFRAMES[pid];
    return { mp4: mp4 || null, iframe: iframe || null };
  }

  async function fetchMp4FromProduct(card) {
    if (window.POSH_DISABLE_VIEW_FETCH === true) return null;
    try {
      const link = card.querySelector('a[href*="/products/"]');
      if (!link) return null;
      const productUrl = link.getAttribute('href');
      if (!productUrl) return null;
      if (_poshAttemptedFetch.has(productUrl)) return null;
      _poshAttemptedFetch.add(productUrl);
      const url = productUrl + (productUrl.includes('?') ? '&' : '?') + 'view=posh-card-video';
      dbg('fetch view', url);
      const res = await fetch(url, {
        credentials: 'same-origin',
        cache: 'no-store',
        headers: {
          // Force Liquid view, not Shopify JSON endpoint
          'Accept': 'text/plain',
          'X-Requested-With': 'XMLHttpRequest'
        }
      });
      dbg('view status', res.status);
      if (!res.ok) return null;
      const text = await res.text();
      // Try parsing as JSON first
      let mp4 = null;
      try {
        const json = JSON.parse(text);
        dbg('view json parsed', !!json && !!json.mp4);
        if (json && json.mp4) mp4 = json.mp4;
      } catch(e) {}
      // Fall back: attempt to extract first mp4 URL from HTML or any response
      if (!mp4) {
        const m = text.match(/(?:https?:)?\/\/[^"'\s>]+\.mp4[^"'\s>]*/i);
        dbg('view html mp4 match', !!m);
        if (m && m[0]) mp4 = m[0];
      }
      return mp4;
    } catch (e) {}
    return null;
  }

  function findImageContainer(card) {
    // Prefer a ratio wrapper if present
    return (
      card.querySelector('.product-block .img-ar') ||
      card.querySelector('.product-block .image-cont') ||
      card.querySelector('.usf-sr-product .img-ar') ||
      card.querySelector('.usf-sr-product .usf-main-img') ||
      card.querySelector('.usf-sr-product .image-cont') ||
      card.querySelector('.usf-sr-product .image-label-wrap') ||
      card
    );
  }

  function createVideoEl(src, poster) {
    const v = document.createElement('video');
    v.src = normalizeUrl(src);
    if (poster) v.poster = poster;
    v.autoplay = true;
    v.setAttribute('autoplay', '');
    v.muted = true;
    // iOS Safari/Chrome require the attribute too
    v.setAttribute('muted', '');
    v.loop = true;
    v.setAttribute('loop', '');
    v.playsInline = true;
    v.setAttribute('playsinline', '');
    v.setAttribute('webkit-playsinline', '');
    v.preload = 'auto';
    v.setAttribute('preload', 'auto');
    v.className = 'collection-card-video';
    v.style.cssText = 'position:absolute;top:0;left:0;width:100%;height:100%;object-fit:cover;display:block;z-index:1;';
    return v;
  }

  function createIframeEl(src) {
    // Ensure strict autoplay params for iOS across providers
    let url = normalizeUrl(src);
    const addParam = (u, k, v = '1') => (new RegExp('[?&]'+k+'(=|&|$)').test(u) ? u : u + (u.includes('?') ? '&' : '?') + k + '=' + v);
    const lower = url.toLowerCase();
    const isYt = /youtube\.com|youtu\.be/.test(lower);
    const isVimeo = /vimeo\.com/.test(lower);
    if (isYt) {
      url = addParam(url, 'autoplay', '1');
      url = addParam(url, 'mute', '1');
      url = addParam(url, 'playsinline', '1');
      url = addParam(url, 'controls', '0');
      url = addParam(url, 'rel', '0');
    } else if (isVimeo) {
      url = addParam(url, 'autoplay', '1');
      url = addParam(url, 'muted', '1');
      url = addParam(url, 'loop', '1');
      // background=1 implies muted autoplay and hides UI on Vimeo
      url = addParam(url, 'background', '1');
      url = addParam(url, 'title', '0');
      url = addParam(url, 'byline', '0');
      url = addParam(url, 'portrait', '0');
    } else {
      // generic fallback
      url = addParam(url, 'autoplay', '1');
      url = addParam(url, 'mute', '1');
      url = addParam(url, 'playsinline', '1');
    }
    const f = document.createElement('iframe');
    f.src = url;
    f.setAttribute('allow', 'autoplay; fullscreen; picture-in-picture');
    f.setAttribute('frameborder', '0');
    f.setAttribute('title', 'Product video');
    f.className = 'collection-card-video';
    f.style.cssText = 'position:absolute;top:0;left:0;width:100%;height:100%;display:block;';
    return f;
  }

  function ensureRatioWrapper(container) {
    // If the container isn't a ratio box, wrap it to preserve layout
    if (!container.classList.contains('img-ar')) {
      const wrapper = document.createElement('div');
      wrapper.className = 'img-ar img-ar--cover';
      // default to 1:1 if we can't know; CSS will clamp min to 0.6
      wrapper.style.setProperty('--aspect-ratio', '1');
      wrapper.style.position = 'relative';

      // Move existing children into wrapper
      while (container.firstChild) wrapper.appendChild(container.firstChild);
      container.appendChild(wrapper);
      return wrapper;
    }
    // Make sure ratio container is positioned for absolute children
    container.style.position = 'relative';
    return container;
  }

  async function injectForCard(card) {
    // Avoid duplicates if media already injected or present
    if (card.querySelector('video.collection-card-video, iframe.collection-card-video')) return;

    const { mp4, iframe } = getMappedVideo(card);
    // Try mapped mp4 first
    let videoSrc = mp4 || null;
    dbg('inject start', { pid: getPid(card), mapped: !!videoSrc, mappedIframe: !!iframe });

    // Fallback from existing <img> if any
    const posterImg = card.querySelector('img');
    if (!videoSrc && posterImg) {
      const dataSrc = posterImg.getAttribute('data-video-src');
      const src = posterImg.getAttribute('src') || '';
      videoSrc = dataSrc || (VIDEO_EXT_REGEX.test(src) ? src.replace(/_\d+x\./, '.') : null);
    }

    // Final fallback: fetch product mp4 via lightweight view
    if (!videoSrc && !iframe) {
      videoSrc = await fetchMp4FromProduct(card);
      dbg('after fetch view', { videoSrc: !!videoSrc });
    }

    if (!videoSrc && !iframe) return; // nothing to do for this card

    const container = ensureRatioWrapper(findImageContainer(card));

    // Remove any existing <img> inside the ratio wrapper to avoid overlay conflicts
    container.querySelectorAll('img').forEach(img => {
      img.style.display = 'none';
    });
    // Hide USF image components that may overlay (custom element or class wrappers)
    container.querySelectorAll('usf-img, .usf-img, .usf-img-wrapper').forEach(n => {
      n.style.display = 'none';
    });

    // If USF uses a background-image element, hide it so our media shows
    if (container.classList.contains('usf-main-img')) {
      container.style.backgroundImage = 'none';
    } else {
      const bgNodes = container.querySelectorAll('.usf-main-img');
      bgNodes.forEach(n => (n.style.display = 'none'));
    }

    // Insert media element
    const mediaEl = videoSrc ? createVideoEl(videoSrc, posterImg?.src) : createIframeEl(iframe);
    // If container already has a media child, prepend to be first
    container.prepend(mediaEl);
    // Nudge iOS to start playback on inline <video>
    if (mediaEl && mediaEl.tagName === 'VIDEO') {
      try { mediaEl.play && mediaEl.play().catch(()=>{}); } catch(e) {}
    }
    dbg('inserted', mediaEl?.tagName);
  }

  function run(root = document) {
    root.querySelectorAll('.usf-sr-product, .product-block').forEach(injectForCard);
  }

  // Initial load
  document.addEventListener('DOMContentLoaded', () => {
    run(document);
    // extra passes for late DOM updates
    setTimeout(() => run(document), 0);
    setTimeout(() => run(document), 300);
    setTimeout(() => run(document), 800);
  });
  window.addEventListener('load', () => {
    run(document);
    setTimeout(() => run(document), 200);
  });
  window.addEventListener('pageshow', (e) => {
    if (e.persisted) run(document);
  });

  // USF custom DOM event (if any)
  document.addEventListener('usf:render', () => run(document));

  // Hook into USF event hub if available
  (function attachUSFListenersWithRetry() {
    let tries = 0;
    const max = 20; // ~10s total
    const iv = setInterval(() => {
      tries++;
      try {
        if (window.usf && usf.event && typeof usf.event.add === 'function') {
          // Populate video map from USF payload metafields, then render
          usf.event.add('sr_dataReceived', (t, data) => {
            try {
              if (data && Array.isArray(data.items)) {
                window.POSH_PRODUCT_VIDEO_SRCS = window.POSH_PRODUCT_VIDEO_SRCS || {};
                data.items.forEach(item => {
                  // Prefer metafield custom.card_video_url if present
                  let cv = null;
                  try { cv = usf.utils && usf.utils.getMetafield ? usf.utils.getMetafield(item, 'custom', 'card_video_url') : null; } catch(e) {}
                  if (cv && /\.(mp4|webm)(\?|$)/i.test(cv)) {
                    window.POSH_PRODUCT_VIDEO_SRCS[item.id] = cv;
                  }
                });
              }
            } catch(e) {}
            run(document);
          });
          usf.event.add(['sr_rendered', 'sr_afterRender'], () => run(document));
          clearInterval(iv);
        }
      } catch (e) {}
      if (tries >= max) clearInterval(iv);
    }, 500);
  })();

  // Expose manual trigger for debugging
  window.POSH_runCollectionVideo = function(){ run(document); };

  // As a last resort, observe USF container for DOM changes
  (function observeUSFWhenReady() {
    function attachObserver(root) {
      if (!window.MutationObserver) return;
      const mo = new MutationObserver(() => run(root));
      mo.observe(root, { childList: true, subtree: true });
    }
    const existing = document.getElementById('usf_container');
    if (existing) {
      attachObserver(existing);
      return;
    }
    let tries = 0;
    const max = 20;
    const iv = setInterval(() => {
      tries++;
      const root = document.getElementById('usf_container');
      if (root) {
        attachObserver(root);
        clearInterval(iv);
      }
      if (tries >= max) clearInterval(iv);
    }, 500);
  })();
})();
